import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";
import { useModuleBEM } from "@b2bportal/core-themes";
import clsx from "clsx";
import {
  type ExpiryYearDigit,
  completeExpiryDate,
  expiryCaretPosition,
  formatExpiryDate,
} from "./formatExpiryDate";
import styles from "./styles.module.scss";

export interface ExpiryDateInputProps
  extends Omit<ControllerRenderProps<FieldValues, string>, "ref">,
    React.AriaAttributes {
  id?: string;
  placeholder?: string;
  className?: string;
  yearDigit?: ExpiryYearDigit;
}

export const ExpiryDateInput = forwardRef<
  HTMLInputElement,
  ExpiryDateInputProps
>((props, formRef) => {
  const {
    className,
    placeholder,
    onChange,
    onBlur,
    yearDigit = "two",
    ...field
  } = props;
  const [display, setDisplay] = useState(
    formatExpiryDate(field.value || "", yearDigit)
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const mergedRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof formRef === "function") formRef(el);
      else if (formRef) formRef.current = el;
    },
    [formRef]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const cursorInRaw = e.target.selectionStart ?? raw.length;
    const formatted = formatExpiryDate(raw, yearDigit);
    const cursor = expiryCaretPosition(raw, cursorInRaw, formatted, yearDigit);

    // Write the formatted value into the DOM now. React only resets a
    // controlled input when the DOM value differs from the prop, so the caret
    // we set here survives the re-render, and also the no-re-render case
    // where the formatted value equals the current display (e.g. deleting
    // the slash), which an effect on `display` would miss.
    e.target.value = formatted;
    e.target.setSelectionRange(cursor, cursor);

    setDisplay(formatted);
    onChange(formatted);
  };

  const handleBlur = () => {
    const completed = completeExpiryDate(display, yearDigit);
    if (completed !== display) {
      setDisplay(completed);
      onChange(completed);
    }
    // After onChange so the blur validation sees the completed value.
    onBlur();
  };

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDisplay(formatExpiryDate(field.value || "", yearDigit));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const [, cn] = useModuleBEM(styles, "ExpiryDateField");

  return (
    <input
      {...field}
      ref={mergedRef}
      placeholder={placeholder}
      inputMode="numeric"
      className={clsx(cn("input"), className)}
      onChange={handleChange}
      onBlur={handleBlur}
      value={display}
    />
  );
});

ExpiryDateInput.displayName = "ExpiryDateInput";
